import clsx from "clsx";
import { useCallback, useState, type CSSProperties } from "react";
import { Dropdown } from "react-bootstrap";
import {
	DIFFICULTY,
	REAL_PLAYERS_INFO,
	WEBSITE_PLAY,
} from "../../common/constants.ts";
import {
	DataTable,
	type DataTableRow,
} from "../components/DataTable/index.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { showNotification } from "../util/showNotification.ts";
import { toWorker } from "../util/toWorker.ts";
import { getCols } from "../../common/getCols.ts";
import type { View } from "../../common/types.ts";
import { choice } from "../../common/random.ts";
import { TeamLogoInline } from "../components/TeamLogoInline.tsx";
import { confirm } from "../util/confirm.tsx";
import { bySport } from "../../common/sportFunctions.ts";
import { relativeTime } from "../util/relativeTime.ts";
import { helpers } from "../util/helpers.ts";
import type { BulkAction } from "../components/DataTable/BulkActions.tsx";

// Re-rendering caused this to run multiple times after "Play" click, even with useRef or useMemo
const randomOtherSport = bySport({
	baseball: choice(["basketball", "football", "hockey"]),
	basketball: choice(["baseball", "football", "hockey"]),
	football: choice(["baseball", "basketball", "hockey"]),
	hockey: choice(["baseball", "basketball", "football"]),
});

const difficultyText = (difficulty: number | undefined) => {
	let prevText: string | undefined;

	if (difficulty === undefined) {
		return "???";
	}

	for (const [text, numeric] of Object.entries(DIFFICULTY)) {
		if (typeof numeric !== "number") {
			throw new Error("Should never happen");
		}

		if (difficulty === numeric) {
			return text;
		}

		// Iteration is in order, so if we're below the value, there will be no direct hit
		if (difficulty < numeric) {
			if (prevText !== undefined) {
				return `${prevText}+`;
			}

			return `${text}-`;
		}

		prevText = text;
	}

	if (prevText !== undefined) {
		return `${prevText}+`;
	}

	return "???";
};

const DifficultyText = ({
	children: difficulty,
}: {
	children: number | undefined;
}) => {
	if (difficulty === undefined) {
		return null;
	}

	return (
		<span
			className={clsx({
				"fw-bold": difficulty > DIFFICULTY.Insane,
				"text-danger": difficulty >= DIFFICULTY.Insane,
			})}
		>
			{difficultyText(difficulty)}
		</span>
	);
};

const PlayButton = ({
	lid,
	disabled,
	throbbing,
	onClick,
}: {
	lid: number;
	disabled: boolean;
	throbbing: boolean;
	onClick: () => void;
}) => {
	if (!disabled && !throbbing) {
		return (
			<a
				className="btn btn-lg btn-success"
				href={`/l/${lid}`}
				onClick={onClick}
			>
				Play
			</a>
		);
	}

	if (throbbing) {
		return (
			<button className="btn btn-lg btn-success dashboard-play-loading">
				Play
			</button>
		);
	}

	return (
		<button className="btn btn-lg btn-success" disabled>
			Play
		</button>
	);
};

const glyphiconStyle = {
	cursor: "pointer",
	fontSize: "larger",
};

const Star = ({ lid, starred }: { lid: number; starred?: boolean }) => {
	const [actuallyStarred, setActuallyStarred] = useState<boolean>(!!starred);
	const toggle = useCallback(async () => {
		setActuallyStarred(!actuallyStarred);
		await toWorker("main", "updateLeague", {
			lid,
			obj: {
				starred: !actuallyStarred,
			},
		});
	}, [actuallyStarred, lid]);

	if (actuallyStarred) {
		return (
			<button
				className="btn btn-link p-0 border-0"
				onClick={toggle}
				style={glyphiconStyle}
			>
				<span className="glyphicon glyphicon-star p-1 text-primary" />
			</button>
		);
	}

	return (
		<button
			className="btn btn-link p-0 border-0"
			onClick={toggle}
			style={glyphiconStyle}
			title="Star"
		>
			<span className="glyphicon glyphicon-star-empty p-1 text-body-secondary" />
		</button>
	);
};

const LeagueName = ({
	lid,
	children: name,
	starred,
	disabled,
	onClick,
}: {
	lid: number;
	children: string;
	starred?: boolean;
	disabled: boolean;
	onClick: () => void;
}) => {
	return (
		<div className="d-flex align-items-center">
			<div className="me-1">
				{!disabled ? (
					<a href={`/l/${lid}`} onClick={onClick}>
						{name}
					</a>
				) : (
					name
				)}
			</div>
			<Star lid={lid} starred={starred} />
		</div>
	);
};

const Ago = ({ date }: { date?: Date }) => {
	if (date) {
		return <span title={date.toLocaleString()}>{relativeTime(date)}</span>;
	}

	return null;
};

// https://stackoverflow.com/a/47417545/786644 hack because of overflow-x in table-responsive, otherwise menu gets chopped off if table has few rows
const dropdownStyle: CSSProperties = {
	position: "static",
};

const Dashboard = ({ leagues }: View<"dashboard">) => {
	const [loadingLID, setLoadingLID] = useState<number | undefined>();
	const [deletingLID, setDeletingLID] = useState<number | undefined>();
	const [cloningLID, setCloningLID] = useState<number | undefined>();
	useTitleBar();

	const cols = getCols(
		[
			"",
			"League",
			"Team",
			"Phase",
			"# Seasons",
			"Difficulty",
			"Created",
			"Last Played",
			"",
		],
		{
			"": {
				width: "1%",
			},
		},
	);

	const rows: DataTableRow<"league">[] = leagues.map((league) => {
		const disabled =
			deletingLID !== undefined ||
			loadingLID !== undefined ||
			cloningLID !== undefined;
		const throbbing = loadingLID === league.lid;
		return {
			key: league.lid,
			metadata: {
				type: "league",
				lid: league.lid,
			},
			data: [
				<PlayButton
					lid={league.lid}
					disabled={disabled}
					throbbing={throbbing}
					onClick={() => setLoadingLID(league.lid)}
				/>,
				<LeagueName
					lid={league.lid}
					starred={league.starred}
					disabled={disabled}
					onClick={() => setLoadingLID(league.lid)}
				>
					{league.name}
				</LeagueName>,
				{
					value: (
						<div className="d-flex align-items-center">
							<TeamLogoInline
								imgURL={league.imgURL}
								size={48}
								className="me-2"
							/>
							<div>
								{league.teamRegion} {league.teamName}
							</div>
						</div>
					),
					sortValue: `${league.teamRegion} ${league.teamName}`,
					classNames: "py-0",
				},
				league.phaseText,
				league.startingSeason !== undefined && league.season !== undefined
					? 1 + league.season - league.startingSeason
					: undefined,
				{
					searchValue: difficultyText(league.difficulty),
					sortValue: league.difficulty,
					value: <DifficultyText>{league.difficulty}</DifficultyText>,
				},
				{
					searchValue: league.created ? relativeTime(league.created) : "",
					sortValue:
						league.created && league.created.getTime
							? league.created.getTime()
							: 0,
					value: <Ago date={league.created} />,
				},
				{
					searchValue: league.lastPlayed ? relativeTime(league.lastPlayed) : "",
					sortValue:
						league.lastPlayed && league.lastPlayed.getTime
							? league.lastPlayed.getTime()
							: 0,
					value: <Ago date={league.lastPlayed} />,
				},
				<Dropdown
					style={dropdownStyle}
					className={window.mobile ? "dropdown-mobile" : undefined}
				>
					<Dropdown.Toggle
						bsPrefix="no-caret"
						id={`dashboard-actions-${league.lid}`}
						style={glyphiconStyle}
						title="Actions"
						variant="link"
						className="p-0 border-0"
					>
						<span className="glyphicon glyphicon-option-vertical text-body-secondary p-2" />
					</Dropdown.Toggle>
					{!disabled ? (
						<Dropdown.Menu>
							<Dropdown.Item href={`/new_league/${league.lid}`}>
								Import
							</Dropdown.Item>
							<Dropdown.Item
								href={`/l/${league.lid}/export_league`}
								onClick={() => setLoadingLID(league.lid)}
							>
								Export
							</Dropdown.Item>
							<Dropdown.Item
								onClick={async () => {
									const newName = await confirm("League name:", {
										defaultValue: league.name,
										okText: "Rename league",
									});

									if (typeof newName === "string") {
										await toWorker("main", "updateLeague", {
											lid: league.lid,
											obj: {
												name: newName,
											},
										});
									}
								}}
							>
								Rename
							</Dropdown.Item>
							<Dropdown.Item
								onClick={async () => {
									try {
										showNotification({
											type: "info",
											text: `Cloning league "${league.name}". This may take a little while if it's a large league.`,
										});

										setCloningLID(league.lid);
										const name = await toWorker(
											"main",
											"cloneLeague",
											league.lid,
										);
										setCloningLID(undefined);

										showNotification({
											type: "info",
											text: `Clone complete! Your new league is named "${name}".`,
										});
									} catch (error) {
										showNotification({
											type: "error",
											text: error.message,
										});
									}
								}}
							>
								Clone
							</Dropdown.Item>
							<Dropdown.Item
								onClick={async () => {
									const proceed = await confirm(
										`Are you sure you want to delete "${league.name}"? You will permanently lose any record of all seasons, players, and games from this league.`,
										{
											okText: "Delete league",
										},
									);

									if (proceed) {
										setDeletingLID(league.lid);
										await toWorker("main", "removeLeague", league.lid);
										setDeletingLID(undefined);
									}
								}}
							>
								Delete
							</Dropdown.Item>
						</Dropdown.Menu>
					) : null}
				</Dropdown>,
			],
		};
	});

	const pagination = rows.length > 100;

	const extraBulkActions: BulkAction<"league">[] = [
		{
			onClick: async (selectedRows) => {
				const lids = Array.from(selectedRows.map.values()).map(
					(metadata) => metadata.lid,
				);

				const proceed = await confirm(
					`Are you sure you want to delete ${helpers.numberWithCommas(lids.length)} ${helpers.plural("league", lids.length)}? You will permanently lose any record of all seasons, players, and games from ${lids.length === 1 ? "this league" : "these leagues"}.`,
					{
						okText: helpers.plural("Delete league", lids.length),
					},
				);

				if (proceed) {
					try {
						for (const lid of lids) {
							setDeletingLID(lid);
							await toWorker("main", "removeLeague", lid);
						}
					} finally {
						setDeletingLID(undefined);
						selectedRows.clear();
					}
				}
			},
			text: "Delete",
			textLong: "Delete leagues",
		},
	];

	return (
		<>
			{location.host.indexOf("beta") === 0 ? (
				<p
					className="alert alert-warning d-inline-block"
					style={{ maxWidth: 840 }}
				>
					You are on the beta site. Sometimes new features are tested on the
					beta site, but most of the time it gets updated less frequently than{" "}
					<a href={`https://${WEBSITE_PLAY}/`}>the main site</a>. So unless
					you're testing some specific thing, you probably should be playing on{" "}
					<a href={`https://${WEBSITE_PLAY}/`}>the main site</a>.
				</p>
			) : null}
			<div className="dashboard-top-wrapper">
				<a
					href="/multiplayer/"
					className="btn btn-success dashboard-top-link me-3 mb-3"
				>
					Multiplayer
					<br />
					<span className="dashboard-top-link-small">
						Criar liga ou entrar por convite
					</span>
				</a>
				{REAL_PLAYERS_INFO ? (
					<>
						<a
							href="/new_league/real"
							className="btn btn-primary dashboard-top-link dashboard-top-link-new me-3 mb-3"
						>
							New league
							<br />
							<span className="dashboard-top-link-small">» Real players</span>
						</a>
						<a
							href="/new_league/random"
							className="btn btn-primary dashboard-top-link dashboard-top-link-new me-sm-3 mb-3"
						>
							New league
							<br />
							<span className="dashboard-top-link-small">» Random players</span>
						</a>
						<div className="d-sm-none" />
						{REAL_PLAYERS_INFO.legends ? (
							<>
								<a
									href="/new_league/cross_era"
									className="btn btn-primary dashboard-top-link dashboard-top-link-new me-3 mb-3"
								>
									New league
									<br />
									<span className="dashboard-top-link-small">» Cross-era</span>
								</a>
								<a
									href="/new_league/legends"
									className="btn btn-primary dashboard-top-link dashboard-top-link-new me-sm-3 mb-3"
								>
									New league
									<br />
									<span className="dashboard-top-link-small">» Legends</span>
								</a>
								<div className="d-sm-none" />
							</>
						) : null}
						<a
							href="/new_league"
							className="btn btn-primary dashboard-top-link dashboard-top-link-new me-3 mb-3"
						>
							New league
							<br />
							<span className="dashboard-top-link-small">» Custom</span>
						</a>
						<a
							href="/exhibition"
							className="btn btn-secondary dashboard-top-link dashboard-top-link-new me-sm-3 mb-3"
						>
							Exhibition game
						</a>
						<div className="d-sm-none" />
					</>
				) : (
					<>
						<a
							href="/new_league"
							className="btn btn-primary dashboard-top-link dashboard-top-link-new me-3 mb-3"
						>
							Create new
							<br />
							league
						</a>
						<a
							href="/exhibition"
							className="btn btn-secondary dashboard-top-link dashboard-top-link-new me-sm-3 mb-3"
						>
							Exhibition game
						</a>
						<div className="d-sm-none" />
					</>
				)}
				<a
					href="https://zengm.com/"
					className={`btn btn-light-bordered dashboard-top-link dashboard-top-link-other mb-3 dashboard-top-link-other-${randomOtherSport} dashboard-top-link-flatten`}
				>
					Try our other sports sim
					<br /> games!
				</a>
			</div>

			{rows.length > 0 ? (
				<DataTable
					cols={cols}
					disableSettingsCache
					extraBulkActions={extraBulkActions}
					defaultSort={[7, "desc"]}
					defaultStickyCols={1}
					name="Dashboard"
					pagination={pagination}
					small={false}
					rows={rows}
				/>
			) : null}
		</>
	);
};

export default Dashboard;
